import * as React from 'react';
import { useTranslation } from 'react-i18next';

export type FieldProps = {
  label?: string;
  requirement?: 'required' | 'optional';
  hint?: string;
  error?: string;
  count?: { value: number; max: number };
  /** Forwarded to the label's htmlFor. */
  htmlFor?: string;
  /** Render the label visually hidden (still read by screen readers). For
   *  compact inline forms where a placeholder carries the meaning — e.g. the
   *  footer newsletter row. */
  hideLabel?: boolean;
  children: React.ReactNode;
};

export function Field({ label, requirement, hint, error, count, htmlFor, hideLabel, children }: FieldProps) {
  const { t } = useTranslation('ui');
  const showFoot = Boolean(error || hint || count);
  return (
    <div className="flex w-full flex-col gap-2.5">
      {label ? (
        <label
          htmlFor={htmlFor}
          className={hideLabel ? 'sr-only' : 'flex items-center gap-2 t-eyebrow-blue'}
        >
          <span>{label}</span>
          {requirement ? (
            <span className="text-[9px] font-medium uppercase tracking-brand text-primary/50">
              {requirement === 'required' ? t('required') : t('optional')}
            </span>
          ) : null}
        </label>
      ) : null}
      {children}
      {showFoot ? (
        <div className="flex items-baseline justify-between gap-3.5">
          {error ? (
            <span className="flex items-center gap-2 text-xs font-medium leading-snug text-error">
              <span aria-hidden="true" className="block h-2 w-2 flex-none rotate-45 bg-error" />
              {error}
            </span>
          ) : hint ? (
            <span className="text-xs leading-snug text-primary/60">{hint}</span>
          ) : (
            <span />
          )}
          {count ? (
            <span className="text-micro font-medium uppercase tracking-tagline text-primary/50 [font-variant-numeric:tabular-nums]">
              {count.value} / {count.max}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
